import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material';
import { Divider } from '@mui/material';
import {
  every,
  filter,
  find,
  isEmpty,
  isObject,
  map,
  toLower,
  values,
} from 'lodash';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog } from 'components';
import { ReceiptValidationType } from 'enums';
import { TranslationNamespace } from 'i18n';
import { formatUtils } from 'utils';

import { ReceiptsValidationSummary } from './ReceiptsValidationSummary';
import { ValidationItem } from './types';

type FileValidationItemProps = {
  filename: string;
  stopScamValidations: ValidationItem[];
  techProtectionValidations: ValidationItem[];
  chequeWalesValidations: ValidationItem[];
};

const FileValidationItem: React.FC<FileValidationItemProps> = ({
  filename,
  stopScamValidations,
  techProtectionValidations,
  chequeWalesValidations,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.receipt_validation',
  });

  const getPerFileResult = useCallback(
    (
      validation: ValidationItem,
    ): { success: boolean; rawValue?: string } | null => {
      if (
        !isObject(validation.details) ||
        !(filename in (validation.details as Record<string, any>))
      ) {
        return null;
      }
      const value = (validation.details as Record<string, any>)[filename];

      if (validation.type === ReceiptValidationType.StopScamDate) {
        return {
          success: validation.success,
          rawValue: value ? formatUtils.formatDate(value) : undefined,
        };
      }
      if (validation.type === ReceiptValidationType.StopScamAmount) {
        return {
          success: validation.success,
          rawValue: value != null ? String(value) : undefined,
        };
      }
      if (validation.type === ReceiptValidationType.StopScamValid) {
        return { success: isEmpty(value) };
      }
      if (validation.type === ReceiptValidationType.TechProtectionRiskScore) {
        const score = isObject(value) ? values(value)[0] : value;
        return {
          success: validation.success,
          rawValue: score != null ? String(score) : undefined,
        };
      }
      if (validation.type === ReceiptValidationType.ChequeWalesVerdict) {
        return {
          success: value === 'original',
          rawValue: value
            ? t(`cheque_wales_verdict.${toLower(value)}` as any, {
                defaultValue: value,
              })
            : undefined,
        };
      }
      return { success: validation.success };
    },
    [filename, t],
  );

  const fileStopScam = useMemo(
    () =>
      filter(
        stopScamValidations,
        (v) =>
          isObject(v.details) && filename in (v.details as Record<string, any>),
      ),
    [stopScamValidations, filename],
  );

  const fileTechProtection = useMemo(
    () =>
      filter(
        techProtectionValidations,
        (v) =>
          isObject(v.details) && filename in (v.details as Record<string, any>),
      ),
    [techProtectionValidations, filename],
  );

  const fileChequeWales = useMemo(
    () =>
      filter(
        chequeWalesValidations,
        (v) =>
          isObject(v.details) && filename in (v.details as Record<string, any>),
      ),
    [chequeWalesValidations, filename],
  );

  const fileSuccess = useMemo(
    () =>
      every(
        [
          ...stopScamValidations,
          ...techProtectionValidations,
          ...chequeWalesValidations,
        ],
        (validation) => {
          const result = getPerFileResult(validation);
          return !result || result.success;
        },
      ),
    [
      stopScamValidations,
      techProtectionValidations,
      chequeWalesValidations,
      getPerFileResult,
    ],
  );

  const renderFileValidations = useCallback(
    (validations: ValidationItem[]) =>
      map(validations, (validation) => {
        const result = getPerFileResult(validation);
        if (!result) {
          return null;
        }
        const label = t(`type.${validation.type}` as any, {
          defaultValue: validation.type,
        });
        return (
          <div key={validation.type} className="tw-text-xs">
            {`${label}: ${formatUtils.formatBoolean(result.success)}`}
            {result.rawValue && (
              <span className="tw-text-gray-500">
                {` (${result.rawValue})`}
              </span>
            )}
          </div>
        );
      }),
    [getPerFileResult, t],
  );

  if (
    isEmpty(fileStopScam) &&
    isEmpty(fileTechProtection) &&
    isEmpty(fileChequeWales)
  ) {
    return null;
  }

  return (
    <div className="tw-mb-3">
      <div className="tw-flex tw-items-center tw-gap-1 tw-mb-1">
        {fileSuccess ? (
          <CheckCircleOutlineIcon color="success" sx={{ fontSize: 16 }} />
        ) : (
          <ErrorOutlineIcon color="error" sx={{ fontSize: 16 }} />
        )}
        <span className="tw-font-medium tw-text-xs">{filename}</span>
      </div>
      {!isEmpty(fileStopScam) && (
        <div className="tw-ml-5 tw-mb-1">
          <div className="tw-text-xs tw-font-bold">{t('stop_scam_title')}</div>
          {renderFileValidations(fileStopScam)}
          {(() => {
            const stopScamValid = find(fileStopScam, {
              type: ReceiptValidationType.StopScamValid,
            });
            if (
              stopScamValid &&
              isObject(stopScamValid.details) &&
              !isEmpty((stopScamValid.details as Record<string, any>)[filename])
            ) {
              const fileVerdicts = (
                stopScamValid.details as Record<string, any>
              )[filename];
              return map(
                Array.isArray(fileVerdicts) ? fileVerdicts : [],
                (verdict: any) => (
                  <div
                    key={verdict?.type}
                    className="tw-text-xs tw-ml-2"
                  >{`- ${t(
                    `stop_scam_verdict.${toLower(verdict?.type)}` as any,
                    { defaultValue: verdict?.type },
                  )}`}</div>
                ),
              );
            }
            return null;
          })()}
        </div>
      )}
      {!isEmpty(fileTechProtection) && (
        <div className="tw-ml-5">
          <div className="tw-text-xs tw-font-bold">
            {t('tech_protection_title')}
          </div>
          {renderFileValidations(fileTechProtection)}
        </div>
      )}
      {!isEmpty(fileChequeWales) && (
        <div className="tw-ml-5">
          <div className="tw-text-xs tw-font-bold">
            {t('cheque_wales_title')}
          </div>
          {renderFileValidations(fileChequeWales)}
        </div>
      )}
    </div>
  );
};

type Props = {
  open: boolean;
  filenames: string[];
  templateValidations: ValidationItem[];
  stopScamValidations: ValidationItem[];
  techProtectionValidations: ValidationItem[];
  chequeWalesValidations: ValidationItem[];
  onClose: () => void;
};

export const ReceiptsValidationDetailsDialog: React.FC<Props> = ({
  open,
  onClose,
  filenames,
  templateValidations,
  stopScamValidations,
  techProtectionValidations,
  chequeWalesValidations,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.receipt_validation',
  });

  return (
    <Dialog
      open={open}
      title={t('title')}
      modal
      maxWidth="sm"
      onClose={onClose}
    >
      <ReceiptsValidationSummary
        templateValidations={templateValidations}
        stopScamValidations={stopScamValidations}
        techProtectionValidations={techProtectionValidations}
        chequeWalesValidations={chequeWalesValidations}
      />

      {!isEmpty(filenames) && (
        <Fragment>
          <Divider className="tw-my-2" />
          <div className="tw-font-bold tw-text-sm tw-mb-2 tw-mt-3">
            {t('per_receipt_title')}
          </div>
          {map(filenames, (filename) => (
            <FileValidationItem
              key={filename}
              filename={filename}
              stopScamValidations={stopScamValidations}
              techProtectionValidations={techProtectionValidations}
              chequeWalesValidations={chequeWalesValidations}
            />
          ))}
        </Fragment>
      )}
    </Dialog>
  );
};
